/**
 * Match report tech (+ optional tech_by_fuel) to vo_flex_tech_catalog rows.
 * Alias style matches simulator_tech_mapping (wildcards + optional Tech|fuel).
 */

/** @typedef {{ techId: string, parentFuel: string, displayName: string, aliases: string[], sortOrder: number, active?: boolean }} VoFlexCatalogRow */

export function normalizeTechNameForMatch(name) {
  if (name == null) return '';
  return String(name).trim().replace(/\s+/g, '_').toLowerCase();
}

function aliasIsWildcard(alias) {
  return String(alias).includes('*');
}

function wildcardToRegExp(normalizedPattern) {
  const escaped = normalizedPattern
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*');
  return new RegExp(`^${escaped}$`, 'i');
}

function aliasMatchesNormalizedName(normalizedName, alias) {
  const normAlias = normalizeTechNameForMatch(alias);
  if (!normAlias) return false;
  if (aliasIsWildcard(alias)) {
    return wildcardToRegExp(normAlias).test(normalizedName);
  }
  return normalizedName === normAlias;
}

/**
 * Build match keys for a report row: compound tech|fuel first, then tech alone.
 * @param {string} tech
 * @param {string|null|undefined} techByFuel
 */
export function reportRowMatchKeys(tech, techByFuel) {
  const t = normalizeTechNameForMatch(tech);
  if (!t) return [];
  const f = normalizeTechNameForMatch(techByFuel);
  const keys = [];
  if (f && f !== '-') keys.push(`${t}|${f}`);
  keys.push(t);
  if (f === '-') keys.push(`${t}|-`);
  return keys;
}

/**
 * @param {string} tech
 * @param {string|null|undefined} techByFuel
 * @param {VoFlexCatalogRow[]} catalog
 * @returns {VoFlexCatalogRow|null}
 */
export function matchReportTechToCatalog(tech, techByFuel, catalog) {
  if (!Array.isArray(catalog) || catalog.length === 0) return null;
  const keys = reportRowMatchKeys(tech, techByFuel);
  if (!keys.length) return null;

  // 1) Exact non-wildcard alias against compound or tech keys (prefer longer alias)
  let bestExact = null;
  let bestExactLen = -1;
  for (const row of catalog) {
    if (row.active === false) continue;
    for (const alias of row.aliases || []) {
      if (aliasIsWildcard(alias)) continue;
      const normAlias = normalizeTechNameForMatch(alias);
      if (!normAlias) continue;
      for (const key of keys) {
        if (key === normAlias && normAlias.length > bestExactLen) {
          bestExactLen = normAlias.length;
          bestExact = row;
        }
      }
    }
  }
  if (bestExact) return bestExact;

  // 2) Wildcard aliases — prefer compound keys, then longer patterns
  let bestWild = null;
  let bestWildScore = -1;
  for (const row of catalog) {
    if (row.active === false) continue;
    for (const alias of row.aliases || []) {
      if (!aliasIsWildcard(alias)) continue;
      const normAlias = normalizeTechNameForMatch(alias);
      for (let ki = 0; ki < keys.length; ki++) {
        const key = keys[ki];
        if (!aliasMatchesNormalizedName(key, alias)) continue;
        // Prefer matching compound key (ki===0 when fuel present), then longer alias
        const score = (keys.length > 1 && ki === 0 ? 10000 : 0) + normAlias.length;
        if (score > bestWildScore) {
          bestWildScore = score;
          bestWild = row;
        }
      }
    }
  }
  return bestWild;
}
